import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { useGetUnitPlans } from '@apps-common/apis/portal/unit'
import {
  UnitActivationPlanCard,
  UnitActivationPlanCardSkeleton
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/components'
import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { FieldErrorMessage } from '@apps-common/components/elements/form'
import { CollapsibleContent, VertSpacerLg, VertSpacerMd } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { UnitPlanUpgradeability } from '@apps-common/config/unit-plan'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'

interface IProps {
  stepNo: number
  loading: boolean
  updating: boolean
  step: ModifyUnitActivationStep
  moveStep: (direction: stepDirection) => void
}

export const UnitActivationPlanForm = ({ stepNo, loading, updating, step, moveStep }: IProps) => {
  const { register, setValue, watch, formState } = useFormContext<IActivationServiceRequestCreateModel>()
  const { errors } = formState

  const productIdVal = watch('productId')
  const unitPlanIdVal = watch('unitPlanId')
  const unitPlanUpgradePathVal = watch('unitPlanUpgradePath')

  const isPlanFixed = useMemo(
    () => !unitPlanUpgradePathVal || unitPlanUpgradePathVal === UnitPlanUpgradeability.fixed,
    [unitPlanUpgradePathVal]
  )

  const {
    isLoading: plansIsLoading,
    isError: plansIsError,
    data: plans
  } = useGetUnitPlans({
    config: {
      enabled: productIdVal?.length > 0
    },
    productId: productIdVal
  })

  useEffect(() => {
    register('unitPlanId', {
      validate: (value) => {
        if (step !== ModifyUnitActivationStep.plan) {
          return true
        }

        if (!value || value.length === 0) {
          return 'Plan required'
        }

        return true
      }
    })
  })

  useEffect(() => {
    if (isPlanFixed) return

    if (!productIdVal || productIdVal?.length === 0) {
      setValue('unitPlanId', '')
    }
  }, [productIdVal])

  const isLoading = loading || plansIsLoading || plansIsError

  return (
    <CollapsibleContent
      title={`Step ${stepNo} | Plan`}
      defaultOpen={step === ModifyUnitActivationStep.plan}
      disabled={step !== ModifyUnitActivationStep.plan}
      classes='bg-surface border border-border rounded-2xl text-text'
    >
      {isPlanFixed && (
        <div className='flex items-center justify-center'>
          <div className='text-[0.85em] leading-[1.1em] mb-[20px] pl-[5px] pr-[10px] py-[5px] rounded-full flex items-center justify-center gap-[7px] jost bg-warning/40 text-on-warning ring-1 ring-warning'>
            <div className='p-1 rounded-full bg-warning'>
              <commonIcons.warning className='text-[1.4em]' />
            </div>
            <div>The selected inactive device has a fixed plan and cannot be changed.</div>
          </div>
        </div>
      )}
      <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-[10px]'>
        <AnimatePresence mode='wait'>
          {isLoading &&
            Array.from(Array(6).keys()).map((k) => (
              <motion.div key={`unit-plan-skeleton-${k}`} {...motionFadeIn(0.2)}>
                <UnitActivationPlanCardSkeleton />
              </motion.div>
            ))}
          {!isLoading &&
            plans &&
            plans?.map((p, i) => (
              <motion.div key={`unit-plan-${p.unitPlanId}`} {...motionFadeIn(0.2)}>
                <UnitActivationPlanCard
                  data={p}
                  planId={unitPlanIdVal}
                  upgradePath={unitPlanUpgradePathVal}
                  onChange={(id) => setValue('unitPlanId', id, { shouldValidate: true })}
                />
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
      <FieldErrorMessage errors={errors?.unitPlanId} />
      <VertSpacerMd />
      <div className='flex items-center justify-end gap-[10px]'>
        <ButtonRoundedSm
          type='button'
          loading={loading || updating}
          text='Previous'
          className='sm:grow-0 action-neutral'
          icon={<commonIcons.arrowAngledLeft />}
          callback={() => moveStep('backward')}
        />
        <ButtonRoundedSm
          type='submit'
          loading={loading || updating}
          text='Next'
          iconRight
          className='sm:grow-0 action-success'
          icon={<commonIcons.arrowRight />}
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
